import * as THREE from "three";

class FilterHelper {

    /**
     * Sets which floor of hotspots to be visible and triggers further filtering for the visible plane.
     * @param hotspotScene Three js scene which contains the hotspots
     * @param hoveredHotspotKey Key of the hotspot currently hovered
     * @param hotspots Text and colors for hotspots.
     * @param hotspotUnavailableColor Color for unavailable hotspots
     * @param currentTags Current tags for filtering
     * @param currentState Number of current state/floor
     * @param currentTypes Array of values to filter by
     * @param currentPricesFilter Array of two price values by which hotspots are filtered
     * @param currentQuadratureFilter Array of two quadrature values by which hotspots are filtered
     * @param currentStateJSON Json data of the current state/floor from the API
     */
    setCurrentPlaneVisibility(hotspotScene, hoveredHotspotKey, hotspots, hotspotUnavailableColor, currentTags, currentState, currentTypes, currentPricesFilter, currentQuadratureFilter, currentStateJSON) {
        hotspotScene.children.forEach((child, index) => {
            if (currentState === index) {
                child.visible = true;
                this.setFilteredHotspotVisibility(child.children, hoveredHotspotKey, hotspots, hotspotUnavailableColor, currentTags, currentTypes, currentPricesFilter, currentQuadratureFilter, index, currentStateJSON);
            } else {
                child.visible = false;
            }
        });
    }

    /**
     * Checks if the tags of an apartment are contained within current filter tags.
     * Returns a boolean.
     * @param currentTags Current tags
     * @param apartmentTags Tags of an apartment
     */
    checkAllApartmentTags(currentTags, apartmentTags) {
        if (currentTags.length !== 0 && apartmentTags.length === 0) return false;

        let numberOfFilters = currentTags.length;
        for (let i = 0; i < apartmentTags.length; i++) {
            if (currentTags.includes(apartmentTags[i])) {
                numberOfFilters--;
            }
        }

        if (numberOfFilters > 0) return false;
        return true;
    }

    /**
     * Checks if the passed apartment is within price and quadrature filters.
     * Returns a boolean.
     * @param apartmentInfo Info of the apartment being filtered
     * @param currentPricesFilter Price range filter values
     * @param currentQuadratureFilter Quadrature range filter values
     */
    checkApartmentPriceAndQuadrature(apartmentInfo, currentPricesFilter, currentQuadratureFilter) {
        let priceBoolean = (apartmentInfo.price >= currentPricesFilter.min && apartmentInfo.price <= currentPricesFilter.max);
        let quadratureBoolean = (apartmentInfo.quadrature >= currentQuadratureFilter.min && apartmentInfo.quadrature <= currentQuadratureFilter.max);
        // Check price filter and quadrature filter
        if (priceBoolean && quadratureBoolean) return true;
        return false;
    }

    /**
     * Checks if an apartment has the type filter which we are filtering by.
     * Returns a boolean.
     * @param currentTypes Array of types we are currently filtering by
     * @param apartmentInfo Info of the apartment we are filtering
     */
    checkFilterTypes(currentTypes, apartmentInfo) {
        if (currentTypes.length === 0 ||
            currentTypes.includes(apartmentInfo.type) ||
            (currentTypes.includes("RESERVED") && apartmentInfo.isReserved)) return true;
        return false;
    }

    extractCode(str) {   
        const regex = /stroke_(.*)/i;
        const match = str.match(regex);
        return match ? match[1] : null;
    }

    /**
     * Filters which hotspots should be visible and sets their colors.
     * @param objects Array of objects loaded from a single fbx 
     * @param hotspotScene Three js scene which contains the hotspots
     * @param hoveredHotspotKey Key of the hotspot currently hovered
     * @param hotspots Text and colors for hotspots.
     * @param hotspotUnavailableColor Color for unavailable hotspots
     * @param currentTags Current tags for filtering
     * @param currentState Number of current state/floor
     * @param currentTypes Array of values to filter by
     * @param currentPricesFilter Array of two price values by which hotspots are filtered
     * @param currentQuadratureFilter Array of two quadrature values by which hotspots are filtered
     * @param floorIndex Index of active state/floor
     * @param currentStateJSON Json data of the current state/floor from the API
     */
    setFilteredHotspotVisibility(objects, hoveredHotspotKey, hotspots, hotspotUnavailableColor, currentTags, currentTypes, currentPricesFilter, currentQuadratureFilter, floorIndex, currentStateJSON) {
        let apartments = null;
        let stroke;

        if (currentStateJSON !== undefined || currentStateJSON !== null) {
            apartments = currentStateJSON[floorIndex].apartments
        }        
        
        objects.forEach((child) => {
            if(child.name.toLowerCase().includes("stroke")) {
                stroke = this.extractCode(child.name.toLowerCase());  
            }

            if (child.isMesh) {
                if (apartments != null) {
                    apartments.forEach((apartment) => {
                        let keyToCompare = apartment.key.includes('_') ? apartment.key.replace('_', '') : apartment.key;
                        let hoverToCompare = hoveredHotspotKey.includes('_') ? hoveredHotspotKey.replace('_', '') : hoveredHotspotKey;

                        const type = apartment.isReserved ? "RESERVED" : apartment.type;
                
                        if ((stroke === keyToCompare.toLowerCase()) &&
                            child.name.toLowerCase().includes("stroke") &&
                            (stroke === hoverToCompare.toLowerCase())) {
                                
                            child.material.color.set(new THREE.Color(hotspots[type].strokeColor));
                            child.material.transparent = false;
                            child.material.opacity = 1;
                            child.material.needsUpdate = true;
                            child.visible = true;
                
                        } else if (stroke && (!hoveredHotspotKey || stroke !== hoverToCompare.toLowerCase()) &&
                                   child.name.toLowerCase().includes("stroke")) {
                                child.visible = false;
                        }

                        if (child.name === apartment.key) {                            

                            if (child.name === hoveredHotspotKey) {                                
                                child.material.opacity = hotspots[type].hoverOpacity;
                                child.material.color.set(new THREE.Color(hotspots[type].defaultColor));
                                return;
                            }

                            if (child.name !== hoveredHotspotKey) {
                                child.material.userData.outlineParameters = {
                                    visible: false,
                                };
                            }

                            if (!apartment.isAvailable) {
                                child.material.opacity = 1;
                                child.material.color.set(hotspotUnavailableColor);
                            }
                            else {
                                let typesOkay = this.checkFilterTypes(currentTypes, apartment);
                                let tagsOkay = this.checkAllApartmentTags(currentTags, apartment.tags);
                                let priceAndQuadratureOkay = this.checkApartmentPriceAndQuadrature(apartment, currentPricesFilter, currentQuadratureFilter);

                                if (currentTypes.length > 0) {
                                    if (typesOkay && tagsOkay && priceAndQuadratureOkay) {
                                        child.material.color.set(new THREE.Color(hotspots[type].defaultColor));
                                        child.material.opacity = hotspots[type].defaultOpacity;
                                    }
                                    else {
                                        child.material.opacity = 0.6;
                                        child.material.color.set(hotspotUnavailableColor);
                                    }
                                }
                                // There are no filter by types => Check rest
                                else {
                                    if (tagsOkay && priceAndQuadratureOkay) {
                                        child.material.color.set(new THREE.Color(hotspots[type].defaultColor));
                                        child.material.opacity = hotspots[type].defaultOpacity;
                                    }
                                    else {
                                        child.material.color.set(hotspotUnavailableColor);
                                        child.material.opacity = 0.6;
                                    }
                                }
                            }
                        }

                    });
                }
            }
        });

    }
}

export default new FilterHelper();